import {v4 as uuidv4} from 'uuid';

// Maximum session duration before forcing refresh (6 hours in ms)
const MAX_SESSION_DURATION = 6 * 60 * 60 * 1000;

// Types for user context
export interface UserContext {
  userId?: string;
  email?: string;
  username?: string;
  organizationId?: string;
  isAnonymous?: boolean; // Track if this is an anonymous user

  // Additional fields from Intercom integration
  planName?: string;
  agencyOrBrand?: string;
  freeTrial?: boolean | string;
  lastPaymentDate?: string;
  nextPaymentDate?: string;
  ottoActivations?: number;
  aiQuota?: number;
  isWhitelabel?: boolean;
  isParentAccount?: boolean;
  promoCode?: string;
  isAdmin?: boolean;
  customerId?: string; // To store customer.id specifically
}

interface SessionContext {
  sessionId: string;
  startTime: number;
  lastActivity: number;
}

// Global storage for context
declare global {
  interface Window {
    __otelUserContext?: UserContext;
    __otelSessionContext?: SessionContext;
    __secureHashCache?: Map<string, string>;
  }
}

/**
 * Initialize session tracking
 * Creates a unique session ID that persists for the browser session
 */
export const initializeSessionTracking = () => {
  if (typeof window === 'undefined') return;

  // Generate a session ID if one doesn't exist or restore from storage
  const initSession = () => {
    // Try to restore from sessionStorage first
    try {
      const storedSessionId = sessionStorage.getItem('otel_session_id');
      const storedStartTime = sessionStorage.getItem('otel_session_start');
      const storedLastActivity = sessionStorage.getItem('otel_session_last_activity');

      if (storedSessionId && storedStartTime) {
        const startTime = parseInt(storedStartTime, 10);
        const lastActivity = storedLastActivity ? parseInt(storedLastActivity, 10) : Date.now();

        // Check if the session has expired (been inactive for too long or exceeded max duration)
        const now = Date.now();
        const sessionDuration = now - startTime;
        const inactivityPeriod = now - lastActivity;

        if (sessionDuration > MAX_SESSION_DURATION || inactivityPeriod > 30 * 60 * 1000) { // 30 minutes inactivity
          // Session expired, create a new one
          return createNewSession(true);
        }

        // Valid session, restore it
        window.__otelSessionContext = {
          sessionId: storedSessionId,
          startTime,
          lastActivity: now,
        };

        // Update last activity time
        try {
          sessionStorage.setItem('otel_session_last_activity', now.toString());
        } catch (e) {
          // Ignore storage errors
        }

        return false; // Not a new session
      }
    } catch (e) {
      // Ignore errors, fallback to new session
    }

    // No valid stored session, create a new one
    return createNewSession(false);
  };

  // Helper function to create a new session
  const createNewSession = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    expired: boolean,
  ) => {
    const sessionId = uuidv4();
    const now = Date.now();

    window.__otelSessionContext = {
      sessionId,
      startTime: now,
      lastActivity: now,
    };

    // Store in sessionStorage to persist across page reloads
    try {
      sessionStorage.setItem('otel_session_id', sessionId);
      sessionStorage.setItem('otel_session_start', now.toString());
      sessionStorage.setItem('otel_session_last_activity', now.toString());
    } catch (e) {
      // sessionStorage might be unavailable in some contexts
      console.debug('[otel-session] Unable to store session in sessionStorage');
    }

    return true; // New session created
  };

  // Initialize the session
  const isNewSession = initSession();

  // Set up activity tracking to keep session fresh
  if (typeof document !== 'undefined') {
    const updateActivity = () => {
      if (window.__otelSessionContext) {
        window.__otelSessionContext.lastActivity = Date.now();
        try {
          sessionStorage.setItem('otel_session_last_activity', Date.now().toString());
        } catch (e) {
          // Ignore storage errors
        }
      }
    };

    // Update on user interaction
    ['click', 'keydown', 'scroll', 'mousemove', 'touchstart'].forEach(event => {
      document.addEventListener(event, updateActivity, {passive: true});
    });

    // Setup periodic session check (every 5 minutes)
    setInterval(() => {
      if (window.__otelSessionContext) {
        const now = Date.now();
        const sessionDuration = now - window.__otelSessionContext.startTime;
        const inactivityPeriod = now - window.__otelSessionContext.lastActivity;

        // If session has been active too long or inactive too long, refresh it
        if (sessionDuration > MAX_SESSION_DURATION || inactivityPeriod > 30 * 60 * 1000) {
          createNewSession(true);
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  // If this is a new session and there's no user context, create an anonymous one
  if (isNewSession && !window.__otelUserContext) {
    setUserContext({
      isAnonymous: true,
      userId: `anon_${sessionId()}`, // Create an anonymous ID based on session
    });
  }
};

/**
 * Set the current user context
 * This should be called when a user logs in or their information changes
 * @param {UserContext} userContext The user context information to set
 */
export const setUserContext = (userContext: UserContext) => {
  if (typeof window === 'undefined') return;

  try {
    // Store the previous context reference before updating
    const previousContext = window.__otelUserContext;

    // Set the new user context
    window.__otelUserContext = userContext;

    // Log context changes in development environment
    if (process.env.NODE_ENV === 'development') {
      console.debug('[otel-user-context] User context updated:', {
        previousUserId: previousContext?.userId,
        currentUserId: userContext?.userId,
        isAnonymous: userContext?.isAnonymous,
      });
    }
  } catch (error) {
    // Catch any unexpected errors to prevent breaking the app
    console.debug?.('[otel-user-context] Error setting user context:', error);
  }
};

/**
 * Get the current session ID
 * @return {string} The current session ID or an empty string if not available
 */
export const sessionId = (): string => {
  if (typeof window === 'undefined') return '';

  if (window.__otelSessionContext) {
    return window.__otelSessionContext.sessionId;
  }

  // Try to get from sessionStorage as fallback
  try {
    const sessionId = sessionStorage.getItem('otel_session_id');
    if (sessionId) {
      return sessionId;
    }
  } catch (e) {
    // sessionStorage might be unavailable
  }

  return '';
};

/**
 * Add user context to a span
 * Utility function to add consistent context to any span
 * @param {any} span The OpenTelemetry span to add context to
 */
export const addContextToSpan = (span: any) => {
  if (typeof window === 'undefined' || !span) return;

  try {
    // Safely set attributes with error handling
    const safeSetAttribute = (key: string, value: any) => {
      if (value === null || value === undefined) return;
      try {
        span.setAttribute(key, value);
      } catch (err) {
        // Silent fail on attribute setting errors
      }
    };

    // Add all user context fields to spans
    if (window.__otelUserContext) {
      const {
        userId,
        email,
        username,
        organizationId,
        isAnonymous,
        planName,
        agencyOrBrand,
        freeTrial,
        lastPaymentDate,
        nextPaymentDate,
        ottoActivations,
        aiQuota,
        isWhitelabel,
        isParentAccount,
        promoCode,
        isAdmin,
        customerId,
      } = window.__otelUserContext;

      // Add user ID if available
      if (userId) {
        safeSetAttribute('user.id', userId);
      }

      // Hash email for privacy before adding to span
      if (email) {
        try {
          safeSetAttribute('user.email_hash', hashPII(email));
        } catch (err) {
          // If hashing fails, don't set the attribute
        }
      }

      // Add username if available
      if (username) {
        safeSetAttribute('user.username', username);
      }

      // Add organization ID if available
      if (organizationId) {
        safeSetAttribute('organization.id', organizationId);
      }

      // Add flag for anonymous users
      if (isAnonymous !== undefined) {
        safeSetAttribute('user.is_anonymous', isAnonymous);
      }

      // Add plan name if available
      if (planName) {
        safeSetAttribute('user.plan', planName);
      }

      // Add agency or brand information
      if (agencyOrBrand) {
        safeSetAttribute('user.agency_or_brand', agencyOrBrand);
      }

      // Add free trial status
      if (freeTrial !== undefined) {
        safeSetAttribute('user.free_trial', freeTrial);
      }

      // Add payment dates
      if (lastPaymentDate) {
        safeSetAttribute('user.last_payment_date', lastPaymentDate);
      }

      if (nextPaymentDate) {
        safeSetAttribute('user.next_payment_date', nextPaymentDate);
      }

      // Add feature usage metrics
      if (ottoActivations !== undefined) {
        safeSetAttribute('user.otto_activations', ottoActivations);
      }

      if (aiQuota !== undefined) {
        safeSetAttribute('user.ai_quota', aiQuota);
      }

      // Add account type flags
      if (isWhitelabel !== undefined) {
        safeSetAttribute('user.is_whitelabel', isWhitelabel);
      }

      if (isParentAccount !== undefined) {
        safeSetAttribute('user.is_parent_account', isParentAccount);
      }

      // Add promo code information
      if (promoCode) {
        safeSetAttribute('user.promo_code', promoCode);
      }

      // Add admin status
      if (isAdmin !== undefined) {
        safeSetAttribute('user.is_admin', isAdmin);
      }

      // Add customer ID
      if (customerId) {
        safeSetAttribute('customer.id', customerId);
      }
    }

    // Add session context if available
    if (window.__otelSessionContext) {
      const {sessionId, startTime} = window.__otelSessionContext;

      if (sessionId) {
        safeSetAttribute('session.id', sessionId);
      }

      // Calculate session duration
      if (startTime) {
        const sessionDurationMs = Date.now() - startTime;
        safeSetAttribute('session.duration_ms', sessionDurationMs);
      }
    }
  } catch (error) {
    // Catch any unexpected errors to prevent breaking the app
    // Don't log here to avoid spam in high-volume contexts
  }
};

/**
 * Fast and secure one-way hash function for PII like emails
 * This creates a hash that can be used for correlation without storing the actual PII
 * @param {string} value The string value to hash
 * @return {string} A hashed version of the input string
 */
const hashPII = (value: string): string => {
  // Use SubtleCrypto if available (more secure) with fallback to simple hash
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    try {
      // This is async, but we want a sync function, so we'll use the simple hash as a fallback
      // and fire off the better hash asynchronously for future spans
      window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
        .then(hashBuffer => {
          // Convert the hash to a hex string
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          // Store for future use
          window.__secureHashCache = window.__secureHashCache || new Map();
          window.__secureHashCache.set(value, hashHex);
        })
        .catch(() => {
          // Fail silently - we already have the fallback
        });
    } catch (e) {
      // Subtle crypto failed, using fallback
    }
  }

  // Check if we already have a secure hash cached
  if (typeof window !== 'undefined' && window.__secureHashCache?.has(value)) {
    return window.__secureHashCache.get(value);
  }

  // Simple fallback hash (more robust than previous version but still fast)
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Get the anonymous user ID for this session
 * Useful for tracking prior to user login/identification
 * @return {string} Anonymous user ID
 */
export const getAnonymousId = (): string => {
  return `anon_${sessionId()}`;
};

/**
 * Refresh the session if it's close to expiration
 * Call this on important state changes to ensure session continuity
 */
export const refreshSession = (): void => {
  if (typeof window === 'undefined' || !window.__otelSessionContext) return;

  const now = Date.now();
  const sessionAge = now - window.__otelSessionContext.startTime;

  // If the session is more than 80% of the way to expiration, refresh it
  if (sessionAge > MAX_SESSION_DURATION * 0.8) {
    // Create a new session
    const sessionId = uuidv4();

    // Update the session
    window.__otelSessionContext = {
      sessionId,
      startTime: now,
      lastActivity: now,
    };

    // Store in sessionStorage to persist across page reloads
    try {
      sessionStorage.setItem('otel_session_id', sessionId);
      sessionStorage.setItem('otel_session_start', now.toString());
      sessionStorage.setItem('otel_session_last_activity', now.toString());
    } catch (e) {
      // sessionStorage might be unavailable in some contexts
    }
  } else {
    // Just update the last activity time
    window.__otelSessionContext.lastActivity = now;
    try {
      sessionStorage.setItem('otel_session_last_activity', now.toString());
    } catch (e) {
      // Ignore storage errors
    }
  }
};
